import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output, Signal } from '@angular/core';
import { IrisDmsFoldersSandbox } from '../../../sandbox/dms-folders.sandbox';
import { Observable, distinctUntilChanged, filter, fromEvent, map, switchMap, take, tap } from 'rxjs';
import { IrisDMSFolder, IrisDMSFolderI } from '../../../models/IrisDMSFolder';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { IrisInfrastructureService } from '@iris/common/services/infrastructure.service';
import { IrisDmsPermissionsSandbox } from '../../../sandbox/dms-permissions.sandbox';
import { IrisDmsDragAndDropHelper } from '../../../helpers/dms-drag-and-drop.helper';
import { IrisDmsFolderRequestStatusStore } from '../../../store/folder-request-status.signal-store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IrisDragAndDropDirective, IrisDragAndDropEventI } from '@iris/common/modules/directives-common/directives/drag-and-drop.directive';
import { MatTreeModule } from '@angular/material/tree';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StrakitButtonComponent, StrakitProgressBarComponent } from '@ngx-strakit/cdk/controls';
import { IrisLoaderModule } from '@iris/common/modules/loader';
import { IrisEmptyContentViewComponent } from '../../empty-content-view/empty-content-view.component';
import { IrisDmsFolderActionsMenuComponent } from '../../actions/folder-actions-menu/folder-actions-menu.component';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { IrisDmsAddFolderButtonComponent } from '../../actions/folder-actions/dms-add-folder-button/dms-add-folder-button.component';
import { StrakitButtonPrefixDirective } from '@ngx-strakit/cdk';

export class DmsMatFoldersTreeFlatNode {
  constructor(
    public item: IrisDMSFolderI,
    public level = 1,
    public expandable = false,
    public isLoading = false,
  ) {}
}

@Component({
  selector: 'iris-dms-mat-folders-tree',
  imports: [
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    TranslatePipe,

    MatTreeModule,
    DragDropModule,
    MatTooltipModule,
    StrakitProgressBarComponent,
    IrisLoaderModule,

    IrisDragAndDropDirective,

    IrisEmptyContentViewComponent,
    IrisDmsFolderActionsMenuComponent,
    IrisDmsAddFolderButtonComponent,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './dms-mat-folders-tree.component.html',
  styleUrls: ['./dms-mat-folders-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsMatFoldersTreeComponent implements OnInit {
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly destroyRef = inject(DestroyRef);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly infrastructureService = inject(IrisInfrastructureService);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly dmsDragAndDropHelper = inject(IrisDmsDragAndDropHelper);
  private readonly translateService = inject(TranslateService);

  private readonly dmsFolderRequestStatusStore = inject(IrisDmsFolderRequestStatusStore);

  readonly tooltipPosition = this.dmsFoldersSandbox.folderTooltipPosition;
  readonly milliSecForClick = 250;

  readonly foldersLoading = computed(() => {
    if (this.initialModalView === this.dmsFoldersSandbox.isModalView()) {
      return this.dmsFoldersSandbox.foldersLoading();
    }
    return false;
  });

  readonly multiselect = input(false);
  readonly isSystem = input(false);
  readonly selectable = input(true);
  readonly external = input(false);
  readonly externalDrop = input(false);

  readonly cdkDataDropped = output<{
    folder: IrisDMSFolderI;
    messagesIds: string[];
    attachmentsIds: string[];
  }>();

  readonly levelAccessor = (node: DmsMatFoldersTreeFlatNode): number => node.level;
  readonly trackBy = (_: number, node: DmsMatFoldersTreeFlatNode): string => node.item.id;

  readonly isFoldersCollectionNotEmpty = computed(() => !!this.dataSource()?.length);
  readonly showNoFoldersMessage = computed(() => !this.isFoldersCollectionNotEmpty() && !this.hasNoSelectedProject);

  readonly selectedFolderId = this.dmsFoldersSandbox.selectedFolderId;
  readonly selectedFolderIdModal = this.dmsFoldersSandbox.selectedFolderIdModal;
  readonly selectedFoldersIdModal = this.dmsFoldersSandbox.selectedFoldersIdsModal;
  readonly isModalView = this.dmsFoldersSandbox.isModalView;
  readonly initialModalView = this.dmsFoldersSandbox.isModalView();
  readonly currentFolderId = computed(() => {
    return this.initialModalView
      ? this.dmsFoldersSandbox.selectedFolderIdModal()
      : this.dmsFoldersSandbox.selectedFolderId();
  });
  readonly currentFolderId$ = toObservable(this.currentFolderId);

  private data: DmsMatFoldersTreeFlatNode[] = [];
  readonly dataSource = computed(() => {
    if (this.dmsFoldersSandbox.isModalView() !== this.initialModalView) {
      return this.data;
    }
    const folders = this.dmsFoldersSandbox.buildVisibleFlatFoldersTree(this.isSystem(), this.initialModalView)();
    const data: DmsMatFoldersTreeFlatNode[] = [];
    folders.forEach(folder => {
      const parentNode = data.find(node => node.item.id === folder.parentId);
      const level = parentNode?.level ?? -1;
      const node = new DmsMatFoldersTreeFlatNode(new IrisDMSFolder(folder), level + 1, folder.hasChildren);
      data.push(node);
    });
    this.data = data;
    return data;
  });

  private isSingleClick = false;

  get hideForTablet(): boolean {
    return this.infrastructureService.isTablet;
  }

  get hasNoSelectedProject(): boolean {
    return !this.dmsFoldersSandbox.getCurrentProjectId();
  }

  ngOnInit(): void {
    if (this.isSystem()) {
      this.dmsFoldersSandbox.getSystemFolders();
    } else {
      this.handleProjectRootFolder();
    }
  }

  getLevel(node: DmsMatFoldersTreeFlatNode): number {
    return node.level;
  };

  isExpandable(node: DmsMatFoldersTreeFlatNode): boolean {
    return node.expandable;
  }

  isSelected = (folder: IrisDMSFolderI): Signal<boolean> => computed(() => {
    if (this.multiselect()) {
      return this.selectedFoldersIdModal().includes(folder.id);
    }
    const folderIdToCheck = this.isModalView() ? this.selectedFolderIdModal() : this.selectedFolderId();
    return folderIdToCheck === folder?.id;
  });

  isExpanded(folderItemNode: DmsMatFoldersTreeFlatNode): boolean {
    return folderItemNode.item.expanded;
  }

  showExpander(folder: IrisDMSFolderI): boolean {
    return folder.hasChildren && !folder.isTrashBin;
  }

  getFolderName(folder: IrisDMSFolderI): string {
    return folder.isRecentFilesFolder
      ? this.translateService.instant(folder.name)
      : folder.name;
  }

  folderDraggable(folder: IrisDMSFolderI): boolean {
    return !this.external() && !folder?.isProjectFolder;
  }

  dataDropped({ dragData, dropData }: IrisDragAndDropEventI, folder: IrisDMSFolderI): void {
    if (this.initialModalView) { return; }
    this.dmsDragAndDropHelper.dataDropped(dragData, dropData, folder.id, folder.name);
  }

  cdkDropListDropped(event: CdkDragDrop<unknown, unknown, { messagesIds?: string[]; attachmentsIds?: string[] }>, folder: IrisDMSFolderI): void {
    this.cdkDataDropped.emit({
      folder,
      messagesIds: event.item.data.messagesIds ?? [],
      attachmentsIds: event.item.data.attachmentsIds ?? [],
    });
  }

  showAddFolderBtn$(folderItemNode: DmsMatFoldersTreeFlatNode, treeNodeElement: HTMLElement): Observable<boolean> {
    return fromEvent(treeNodeElement, 'mouseenter', { once: true }).pipe(
      map(() => {
        if (!this.showAddFolderBtn(folderItemNode.item)) {
          return false;
        }
        this.dmsPermissionsSandbox.getUserAvailableActions(folderItemNode.item.id);
        return this.dmsPermissionsSandbox.hasUpdatePermissionForFolderId(folderItemNode.item.id)();
      }),
      take(1),
    );
  }

  folderTreeItemClicked(folder: IrisDMSFolderI): void {
    this.isSingleClick = true;
    setTimeout(() => {
      if (this.isSingleClick) {
        this.setSelectedFolder(folder);
      }
    }, this.milliSecForClick);
  }

  dblClickToggleFolder(folderItemNode: DmsMatFoldersTreeFlatNode): boolean | void {
    this.isSingleClick = false;
    this.toggleNode(folderItemNode, !folderItemNode.item.expanded);
  }

  getFolderIconClass(folder: IrisDMSFolderI): string {
    if (folder.isTrashBin) {
      return 'far fa-trash-alt trash-icon';
    }
    const iconCss = folder.isParentLocked ? 'fas virtual-lock' : 'fas';
    return folder.isExpanded ? `${iconCss} fa-folder-open` : `${iconCss} fa-folder`;
  }

  hasFolderInProgressAction(folderId: string): Signal<boolean> {
    return computed(() => {
      if (this.dmsFoldersSandbox.isModalView() !== this.initialModalView) {
        return false;
      }
      return this.dmsFolderRequestStatusStore.hasInProgressAction(folderId)();
    });
  }

  toggleNode(node: DmsMatFoldersTreeFlatNode, expand: boolean) : void {
    if (!node.item.hasChildren && expand || node.item.isExpanded === expand) {
      return;
    }

    if (!expand) {
      this.dmsFoldersSandbox.unExpandFolder(node.item.id, node.item.isSystem);
      return;
    }
    node.isLoading = true;
    this.dmsFoldersSandbox.expandFolder(node.item.id, node.item.isSystem);
  }

  private handleProjectRootFolder(): void {
    this.globalSandbox.currentProjectId$.pipe(
      distinctUntilChanged(),
      filter(() => !this.initialModalView),
      tap(projectId => {
        if (projectId) {
          this.dmsFoldersSandbox.getProjectRootFolder(projectId);
        } else if(this.dataSource().length) {
          this.dmsFoldersSandbox.resetSelectedFolderId();
          this.dmsFoldersSandbox.resetFolders();
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    this.dmsFoldersSandbox.currentProjectRootFolderId$.pipe(
      distinctUntilChanged(),
      tap(() => this.expandRootFolder()),
      filter(Boolean),
      switchMap((folderId) => this.dmsPermissionsSandbox.getUserAvailableActions$(folderId)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private showAddFolderBtn(folder: IrisDMSFolderI): boolean {
    return !folder.isTrashBin && !folder.isRecentFilesFolder && this.folderAndParentFolderAreNotLocked(folder);
  }

  private folderAndParentFolderAreNotLocked(folder: IrisDMSFolderI): boolean {
    return !folder.isLocked && !folder.isParentLocked;
  }

  private setSelectedFolder(folder: IrisDMSFolderI): void {
    if (!this.selectable()) { return; }
    const selected = this.isSelected(folder);
    if (selected()) {
      return;
    }

    if (this.initialModalView) {
      if (this.multiselect()) {
        this.dmsFoldersSandbox.toggleFolderInSelectedListModal(folder.id);
        return;
      }
      this.dmsFoldersSandbox.setSelectedFolderModal(folder.id, true);
      return;
    }
    this.dmsFoldersSandbox.setSelectedFolder(folder.id, true);
  }

  private expandRootFolder(): void {
    this.dmsFoldersSandbox.currentProjectRootFolder$.pipe(
      filter(Boolean),
      filter(folder => !folder.expanded && folder.hasChildren),
      take(1),
      tap(folder => this.dmsFoldersSandbox.expandFolder(folder?.id, folder?.isSystem)),
    ).subscribe();
  }
}
